// Global, project and user-level settings (architecture sprint-11 §8.3, §8.5, §9.7). A settings change
// invalidates the timer (rules), entries (editable), projects (rules) and the settings key itself.
import { useMutation, useQuery, useQueryClient, type QueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import { apiRequest, apiRequestNoContent } from '@/lib/api';
import type { GlobalSetting, MeSetting, ProjectSetting, ProjectSettings, SettingKey, SettingModeValue } from '@/types/settings';

export const GLOBAL_SETTINGS_KEY = ['admin', 'settings'] as const;
export const ME_SETTINGS_KEY = ['me', 'settings'] as const;
export const projectSettingsKey = (projectId: string): readonly unknown[] => ['project-settings', projectId];

async function invalidateRuleConsumers(queryClient: QueryClient): Promise<void> {
  await Promise.all(
    [['timer'], ['entries'], ['projects'], ['me'], ['admin', 'tracker'], ['oversight']].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
  );
}

// ── global ──────────────────────────────────────────────────────────────────

export function useGlobalSettings(): UseQueryResult<GlobalSetting[]> {
  return useQuery({
    queryKey: GLOBAL_SETTINGS_KEY,
    queryFn: async () => (await apiRequest<{ items: GlobalSetting[] }>('/api/admin/settings')).items,
  });
}

export function useSetGlobalSetting(): UseMutationResult<GlobalSetting, Error, { key: SettingKey; value: unknown }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value }) => apiRequest<GlobalSetting>(`/api/admin/settings/${key}`, { method: 'PUT', body: JSON.stringify({ value }) }),
    onSuccess: async () => {
      await Promise.all([queryClient.invalidateQueries({ queryKey: GLOBAL_SETTINGS_KEY }), invalidateRuleConsumers(queryClient)]);
    },
  });
}

export function useResetGlobalSetting(): UseMutationResult<void, Error, SettingKey> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (key) => apiRequestNoContent(`/api/admin/settings/${key}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await Promise.all([queryClient.invalidateQueries({ queryKey: GLOBAL_SETTINGS_KEY }), invalidateRuleConsumers(queryClient)]);
    },
  });
}

// ── project (oversight mount; Admins and delegated Supervisors) ─────────────

export function useProjectSettings(projectId: string, enabled = true): UseQueryResult<ProjectSettings> {
  return useQuery({
    queryKey: projectSettingsKey(projectId),
    queryFn: () => apiRequest<ProjectSettings>(`/api/oversight/projects/${projectId}/settings`),
    enabled,
    retry: false,
  });
}

export interface ProjectSettingInput {
  key: SettingKey;
  value: unknown;
  mode?: SettingModeValue;
}

export function useSetProjectSetting(projectId: string): UseMutationResult<ProjectSetting, Error, ProjectSettingInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value, mode }) =>
      apiRequest<ProjectSetting>(`/api/oversight/projects/${projectId}/settings/${key}`, {
        method: 'PUT',
        body: JSON.stringify(mode === undefined ? { value } : { value, mode }),
      }),
    onSuccess: async () => {
      await Promise.all([queryClient.invalidateQueries({ queryKey: projectSettingsKey(projectId) }), invalidateRuleConsumers(queryClient)]);
    },
  });
}

export function useResetProjectSetting(projectId: string): UseMutationResult<void, Error, SettingKey> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (key) => apiRequestNoContent(`/api/oversight/projects/${projectId}/settings/${key}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await Promise.all([queryClient.invalidateQueries({ queryKey: projectSettingsKey(projectId) }), invalidateRuleConsumers(queryClient)]);
    },
  });
}

// ── user level ──────────────────────────────────────────────────────────────

export function useMeSettings(): UseQueryResult<MeSetting[]> {
  return useQuery({
    queryKey: ME_SETTINGS_KEY,
    queryFn: async () => (await apiRequest<{ items: MeSetting[] }>('/api/me/settings')).items,
  });
}
